/**
 * FileInputMenu Component
 *
 * Dropdown menu for selecting file input method:
 * 1. Upload from computer (with drag-and-drop)
 * 2. Select from media library
 *
 * Used in campaign creation and other contexts requiring media selection
 */

import React, { useState, useRef, useEffect } from 'react';
import { Upload, Image as ImageIcon, ChevronDown, Link } from 'lucide-react';
import { MediaSelector } from './MediaSelector';
import { MediaUploadZone } from './MediaUploadZone';
import { MediaAsset } from '@/hooks/useMediaUpload';

interface FileInputMenuProps {
  onFilesSelected: (files: File[]) => void;
  onLibrarySelected?: (assets: MediaAsset[]) => void;
  onUrlSelected?: (url: string) => void;
  onError?: (error: string) => void;
  buttonText?: string;
  mediaType?: 'image' | 'video' | 'all';
  maxSelection?: number;
  multiSelect?: boolean;
  className?: string;
  compact?: boolean;
}

export const FileInputMenu: React.FC<FileInputMenuProps> = ({
  onFilesSelected,
  onLibrarySelected,
  onUrlSelected,
  onError,
  buttonText = 'Add Media',
  mediaType = 'all',
  maxSelection,
  multiSelect = true,
  className = '',
  compact = false
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  const handleUploadClick = () => {
    setIsMenuOpen(false);
    fileInputRef.current?.click();
  };

  const handleLibraryClick = () => {
    setIsMenuOpen(false);
    setShowLibraryModal(true);
  };

  const handleUrlClick = () => {
    setIsMenuOpen(false);
    setShowUrlInput(true);
    setUrlValue('');
    setTimeout(() => urlInputRef.current?.focus(), 50);
  };

  const handleUrlSubmit = () => {
    const url = urlValue.trim();
    if (!url) return;
    if (onUrlSelected) {
      onUrlSelected(url);
    }
    setShowUrlInput(false);
    setUrlValue('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      onFilesSelected(files);  // Pass File objects directly, no upload
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleLibrarySelect = (assets: MediaAsset[]) => {
    setShowLibraryModal(false);

    // Phase 3.4: Always use onLibrarySelected if provided
    if (onLibrarySelected) {
      onLibrarySelected(assets);
    } else {
      // Legacy fallback for contexts that don't support library linking yet
      // Fetch files from library and pass to onFilesSelected
      Promise.all(
        assets.map(async (asset) => {
          try {
            const response = await fetch(asset.public_url);
            const blob = await response.blob();
            return new File(
              [blob],
              asset.original_filename || asset.filename,
              { type: asset.mime_type }
            );
          } catch (error) {
            console.error(`Failed to fetch ${asset.filename}:`, error);
            return null;
          }
        })
      ).then(files => {
        const validFiles = files.filter(f => f !== null) as File[];
        onFilesSelected(validFiles);
      });
    }
  };

  return (
    <>
      <div className={`relative ${className}`} ref={menuRef}>
        {/* Main Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={
            compact
              ? "p-1.5 text-ink-3 hover:text-primary dark:hover:text-primary rounded-lg hover:bg-inset transition-colors"
              : "px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center gap-2 transition-colors"
          }
          title={compact ? "Attach media" : undefined}
        >
          <Upload size={compact ? 20 : 20} />
          {!compact && (
            <>
              <span>{buttonText}</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
              />
            </>
          )}
        </button>

        {/* Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute bottom-full mb-2 right-0 w-64 bg-card dark:bg-card-darkAlt rounded-lg shadow-xl border border-line dark:border-border-dark py-2 z-[9999]">
            <button
              onClick={handleUploadClick}
              className="w-full px-4 py-3 text-left hover:bg-ground flex items-center gap-3 transition-colors"
            >
              <div className="w-10 h-10 bg-work-soft rounded-lg flex items-center justify-center">
                <Upload size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-ink">Upload from Computer</div>
                <div className="text-xs text-ink-3">Browse or drag & drop files</div>
              </div>
            </button>

            <div className="my-1 border-t border-line dark:border-border-dark"></div>

            <button
              onClick={handleLibraryClick}
              className="w-full px-4 py-3 text-left hover:bg-ground flex items-center gap-3 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ImageIcon size={20} className="text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-ink">Select from Library</div>
                <div className="text-xs text-ink-3">Choose existing media</div>
              </div>
            </button>

            <div className="my-1 border-t border-line dark:border-border-dark"></div>

            <button
              onClick={handleUrlClick}
              className="w-full px-4 py-3 text-left hover:bg-ground flex items-center gap-3 transition-colors"
            >
              <div className="w-10 h-10 bg-ok-soft rounded-lg flex items-center justify-center">
                <Link size={20} className="text-ok" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-ink">Add from URL</div>
                <div className="text-xs text-ink-3">Paste an image link</div>
              </div>
            </button>
          </div>
        )}

        {/* Inline URL input panel */}
        {showUrlInput && (
          <div className="absolute bottom-full mb-2 right-0 w-72 bg-card dark:bg-card-darkAlt rounded-lg shadow-xl border border-line dark:border-border-dark p-3 z-[9999]">
            <p className="text-xs font-medium text-ink-2 mb-2">Paste image URL</p>
            <input
              ref={urlInputRef}
              type="url"
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleUrlSubmit(); }
                if (e.key === 'Escape') { setShowUrlInput(false); setUrlValue(''); }
              }}
              placeholder="https://example.com/image.jpg"
              className="w-full text-sm px-3 py-2 rounded-md border border-line dark:border-border-dark bg-card dark:bg-surface text-ink placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleUrlSubmit}
                disabled={!urlValue.trim()}
                className="flex-1 py-1.5 text-xs font-medium bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-40 transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => { setShowUrlInput(false); setUrlValue(''); }}
                className="flex-1 py-1.5 text-xs font-medium border border-line dark:border-border-dark text-ink-2 rounded-md hover:bg-ground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Library Selector Modal */}
      <MediaSelector
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        onSelect={handleLibrarySelect}
        multiSelect={multiSelect}
        mediaType={mediaType}
        maxSelection={maxSelection}
        defaultGroup="uploads"
      />
    </>
  );
};
